import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, FolderOpen, Play, User, Globe, Coins, CreditCard, Receipt, Share2, Send, LogOut, ChevronDown } from 'lucide-react';
import { useAuthStore, useAppStore } from '../store';
import { T, FLAGS } from '../i18n';
import AuthModal from '../components/AuthModal';
import LanguageModal from '../components/LanguageModal';
import SubscriptionModalV2 from '../components/SubscriptionModalV2';
import { extractBase64FromDataUrl, readImageFileAsDataUrl } from '../utils/photoImport';

export default function HomePage() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { lang, setPhoto } = useAppStore();
  const t = T[lang] || T.fr;
  
  const [showAuth, setShowAuth] = useState(false);
  const [showLang, setShowLang] = useState(false);
  const [showSub, setShowSub] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  
  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const userMenuRef = useRef(null);
  
  // Click outside to close user menu
  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  
  const handleTakePhoto = () => {
    cameraInputRef.current?.click();
  };
  
  const handleImportPhoto = () => {
    fileInputRef.current?.click();
  };
  
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const dataUrl = await readImageFileAsDataUrl(file);
      const base64 = extractBase64FromDataUrl(dataUrl);
      setPhoto(URL.createObjectURL(file), base64);
      navigate('/result');
    } finally {
      e.target.value = '';
    }
  };
  
  const handleLogout = () => {
    setShowUserMenu(false);
    logout();
  };
  
  const handleDemo = () => {
    setPhoto(null, null);
    navigate('/result');
  };
  
  const handleProjects = () => {
    if (!user) {
      setShowAuth(true);
    } else {
      navigate('/projects');
    }
  };
  
  return (
    <div className="h-full flex flex-col">
      {/* Hidden inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFileSelect}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileSelect}
      />
      
      {/* Header */}
      <header className="h-14 glass flex items-center justify-between px-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowLang(true)}
            className="text-2xl touch-feedback"
            data-testid="lang-btn"
          >
            {FLAGS[lang]}
          </button>
          <button
            onClick={() => user ? setShowSub(true) : setShowAuth(true)}
            className="flex items-center gap-1 text-primary font-bold text-sm"
            data-testid="credits-btn"
          >
            <Coins size={16} />
            <span>{user?.credits ?? 0}</span>
          </button>
        </div>
        
        <div className="font-display text-center leading-none">
          <span className="text-secondary font-bold text-sm">SoView</span>
          <br />
          <span className="text-primary font-bold text-sm">FX</span>
        </div>
        
        <div className="flex items-center gap-2 relative" ref={userMenuRef}>
          <button
            onClick={() => user ? setShowUserMenu(!showUserMenu) : setShowAuth(true)}
            className="p-2 rounded-lg hover:bg-white/5 transition-colors"
            data-testid="auth-btn"
          >
            {user ? (
              <div className="flex items-center gap-1">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center text-white text-xs font-bold">
                    {user.name?.[0]?.toUpperCase()}
                  </div>
                )}
                <ChevronDown size={14} className={`text-muted-foreground transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
              </div>
            ) : (
              <User size={20} className="text-secondary" />
            )}
          </button>
          
          <AnimatePresence>
            {showUserMenu && user && (
              <motion.div
                initial={{ opacity: 0, y: -8, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.95 }}
                transition={{ duration: 0.15 }}
                className="absolute top-full right-0 mt-2 w-56 rounded-xl bg-black/80 backdrop-blur-xl border border-white/10 shadow-xl overflow-hidden z-50"
              >
                {/* User header */}
                <div className="px-4 py-3 border-b border-white/10">
                  <p className="text-sm font-medium text-white truncate">{user.name || user.email}</p>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                </div>
                
                {/* Menu items */}
                <div className="py-1">
                  <button
                    onClick={() => { setShowUserMenu(false); navigate('/subscription'); }}
                    className="w-full px-4 py-2.5 flex items-center gap-3 text-sm text-white/80 hover:text-white hover:bg-white/5 transition-colors text-left"
                  >
                    <CreditCard size={16} className="text-secondary" />
                    Mes abonnements
                  </button>
                  <button
                    onClick={() => { setShowUserMenu(false); navigate('/invoices'); }}
                    className="w-full px-4 py-2.5 flex items-center gap-3 text-sm text-white/80 hover:text-white hover:bg-white/5 transition-colors text-left"
                  >
                    <Receipt size={16} className="text-primary" />
                    Mes factures
                  </button>
                  <button
                    onClick={() => { setShowUserMenu(false); navigate('/shares'); }}
                    className="w-full px-4 py-2.5 flex items-center gap-3 text-sm text-white/80 hover:text-white hover:bg-white/5 transition-colors text-left"
                  >
                    <Share2 size={16} className="text-emerald-400" />
                    Mes partages
                  </button>
                  <button
                    onClick={() => { setShowUserMenu(false); navigate('/send-simulation'); }}
                    className="w-full px-4 py-2.5 flex items-center gap-3 text-sm text-white/80 hover:text-white hover:bg-white/5 transition-colors text-left"
                  >
                    <Send size={16} className="text-amber-400" />
                    Envoyer une simulation
                  </button>
                  <button
                    onClick={() => { setShowUserMenu(false); navigate('/share-history'); }}
                    className="w-full px-4 py-2.5 flex items-center gap-3 text-sm text-white/80 hover:text-white hover:bg-white/5 transition-colors text-left"
                  >
                    <Send size={16} className="text-blue-400" />
                    Projets partagés
                  </button>
                  <button
                    onClick={() => { setShowUserMenu(false); navigate('/email-template'); }}
                    className="w-full px-4 py-2.5 flex items-center gap-3 text-sm text-white/80 hover:text-white hover:bg-white/5 transition-colors text-left"
                  >
                    <Send size={16} className="text-purple-400" />
                    Modèle d'email
                  </button>
                </div>
                
                {/* Logout */}
                <div className="border-t border-white/10">
                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-2.5 flex items-center gap-3 text-sm text-red-400 hover:text-red-300 hover:bg-white/5 transition-colors text-left"
                  >
                    <LogOut size={16} />
                    Déconnexion
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </header>
      
      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-safe">
        {/* Take Photo Card */}
        <motion.button
          onClick={handleTakePhoto}
          className="w-full card p-4 flex items-center gap-4 text-left touch-feedback"
          whileTap={{ scale: 0.98 }}
          data-testid="take-photo-btn"
        >
          <div className="w-14 h-14 rounded-xl bg-secondary/20 flex items-center justify-center">
            <Camera size={28} className="text-secondary" />
          </div>
          <div>
            <p className="font-display font-bold text-sm tracking-wide uppercase">{t.take_photo}</p>
            <p className="text-xs text-muted-foreground mt-1">{t.take_photo_sub}</p>
          </div>
        </motion.button>
        
        {/* Import Photo Card */}
        <motion.button
          onClick={handleImportPhoto}
          className="w-full card p-4 flex items-center gap-4 text-left touch-feedback"
          whileTap={{ scale: 0.98 }}
          data-testid="import-photo-btn"
        >
          <div className="w-14 h-14 rounded-xl bg-primary/20 flex items-center justify-center">
            <FolderOpen size={28} className="text-primary" />
          </div>
          <div>
            <p className="font-display font-bold text-sm tracking-wide uppercase">{t.import_photo}</p>
            <p className="text-xs text-muted-foreground mt-1">{t.import_photo_sub}</p>
          </div>
        </motion.button>
        
        {/* My Projects Card */}
        <motion.button
          onClick={handleProjects}
          className="w-full card p-4 flex items-center gap-4 text-left touch-feedback"
          whileTap={{ scale: 0.98 }}
          data-testid="projects-btn"
        >
          <div className="w-14 h-14 rounded-xl bg-white/5 flex items-center justify-center">
            <FolderOpen size={28} className="text-muted-foreground" />
          </div>
          <div>
            <p className="font-display font-bold text-sm tracking-wide uppercase">{t.my_projects}</p>
            <p className="text-xs text-muted-foreground mt-1">{t.my_projects_sub}</p>
          </div>
        </motion.button>
        
        {/* Mini cards row */}
        <div className="grid grid-cols-2 gap-3">
          {/* Demo Card */}
          <motion.button
            onClick={handleDemo}
            className="card p-3 flex items-center gap-3 text-left touch-feedback"
            whileTap={{ scale: 0.98 }}
            data-testid="demo-btn"
          >
            <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center">
              <Play size={20} className="text-secondary" />
            </div>
            <div>
              <p className="font-display font-bold text-xs">{t.demo}</p>
              <p className="text-[10px] text-muted-foreground">{t.demo_sub}</p>
            </div>
          </motion.button>
          
          {user && (
            <>
              <motion.button
                onClick={() => navigate('/subscription')}
                className="card p-3 flex items-center gap-3 text-left touch-feedback"
                whileTap={{ scale: 0.98 }}
              >
                <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center">
                  <CreditCard size={20} className="text-secondary" />
                </div>
                <div>
                  <p className="font-display font-bold text-xs">Abonnement</p>
                  <p className="text-[10px] text-muted-foreground">Crédits & forfaits</p>
                </div>
              </motion.button>
              <motion.button
                onClick={() => navigate('/invoices')}
                className="card p-3 flex items-center gap-3 text-left touch-feedback"
                whileTap={{ scale: 0.98 }}
              >
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Receipt size={20} className="text-primary" />
                </div>
                <div>
                  <p className="font-display font-bold text-xs">Factures</p>
                  <p className="text-[10px] text-muted-foreground">Historique</p>
                </div>
              </motion.button>
              <motion.button
                onClick={() => navigate('/shares')}
                className="card p-3 flex items-center gap-3 text-left touch-feedback"
                whileTap={{ scale: 0.98 }}
              >
                <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                  <Share2 size={20} className="text-emerald-400" />
                </div>
                <div>
                  <p className="font-display font-bold text-xs">Partages</p>
                  <p className="text-[10px] text-muted-foreground">Simulations partagées</p>
                </div>
              </motion.button>
              <motion.button
                onClick={() => navigate('/send-simulation')}
                className="card p-3 flex items-center gap-3 text-left touch-feedback"
                whileTap={{ scale: 0.98 }}
              >
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <Send size={20} className="text-amber-400" />
                </div>
                <div>
                  <p className="font-display font-bold text-xs">Envoyer</p>
                  <p className="text-[10px] text-muted-foreground">Simulation</p>
                </div>
              </motion.button>
              <motion.button
                onClick={() => navigate('/share-history')}
                className="card p-3 flex items-center gap-3 text-left touch-feedback"
                whileTap={{ scale: 0.98 }}
              >
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Share2 size={20} className="text-blue-400" />
                </div>
                <div>
                  <p className="font-display font-bold text-xs">Partages Email</p>
                  <p className="text-[10px] text-muted-foreground">Historique</p>
                </div>
              </motion.button>
            </>
          )}
        </div>
        
        {/* User info */}
        {user && (
          <div className="mt-4 p-3 rounded-xl bg-white/5 text-center">
            <p className="text-sm text-muted-foreground">
              {t.login}: <span className="text-white font-medium">{user.email}</span>
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {user.credits} {t.credits_remaining}
            </p>
          </div>
        )}
      </div>
      
      {/* Modals */}
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
      {showLang && <LanguageModal onClose={() => setShowLang(false)} />}
      {showSub && <SubscriptionModalV2 onClose={() => setShowSub(false)} />}
    </div>
  );
}
